import { useEffect, type JSX, type RefObject } from 'react';
import {
  effectiveGain,
  useAudioSettingsStore,
  type AudioChannel,
  type ChannelPrefs,
} from '../../state/audio-settings.store';
import { peerGainRecord, usePeerVolumeStore } from '../../state/peer-volume.store';
import { setSoundboardVolume } from './sound/soundboard-audio';
import { setEntrywayVolume } from './sound/entryway-audio';
import type { VoiceClient } from '../../net/voice-client';

/**
 * The per-device AUDIO MIX — one independent fader per sound channel (effects / voices / radio), and the
 * glue that pushes each channel's level down to its engine. Two exports:
 *   • {@link useApplyAudioSettings} — a hook (mounted once in OfficePage) that reflects store changes into
 *     the SFX engines + the live {@link VoiceClient} (the channel mix AND the per-participant personal voice
 *     trims from {@link usePeerVolumeStore}). Radio is intentionally NOT driven here: the
 *     {@link import('./radio/RadioCard').RadioCard} owns the YouTube player and applies the same `radio`
 *     channel itself (so there's exactly one apply path for it).
 *   • {@link AudioSettingsPanel} — the three sliders shown in the HUD "Me" menu's Audio section.
 *
 * LOCAL & per-device, never shared (Constitution Art. 1) — comfort knobs, not a KPI.
 */

// ── Apply: store → engines ─────────────────────────────────────────────────────────────────────────

export function useApplyAudioSettings(voiceRef: RefObject<VoiceClient | null>): void {
  const sfx = useAudioSettingsStore((s) => s.sfx);
  const voice = useAudioSettingsStore((s) => s.voice);
  const media = useAudioSettingsStore((s) => s.radio);
  const peerVolumes = usePeerVolumeStore((s) => s.volumes);

  // One "Sound effects" fader drives BOTH effect engines (recorded soundboard + procedural entryway).
  useEffect(() => {
    const gain = effectiveGain(sfx);
    setSoundboardVolume(gain);
    setEntrywayVolume(gain);
  }, [sfx]);

  // Listener-side master over the whole voice channel. No-op until voice is live; the VoiceClient also
  // re-applies the remembered level itself across a reconnect, and OfficePage seeds it at client creation.
  useEffect(() => {
    voiceRef.current?.setVoiceVolume(effectiveGain(voice));
  }, [voice, voiceRef]);

  // The "Media" channel (ADR-0033, the old `radio` key) drives the Media Hub's SCREEN-SHARE audio gain on
  // the voice client. The YouTube IFrame reads the same store value directly in the card, so one fader
  // governs all media sound (YouTube + a shared screen).
  useEffect(() => {
    voiceRef.current?.setMediaGain(effectiveGain(media));
  }, [media, voiceRef]);

  // Per-participant PERSONAL voice trims (Discord-style) — push the whole userId→multiplier map to the live
  // VoiceClient whenever a slider moves, so the change is heard at once. OfficePage seeds it at client
  // creation (the effect doesn't re-fire when voiceRef.current is swapped on a reconnect). Output-only.
  useEffect(() => {
    voiceRef.current?.setPeerGains(peerGainRecord(peerVolumes));
  }, [peerVolumes, voiceRef]);
}

// ── Panel: the three faders ──────────────────────────────────────────────────────────────────────

interface ChannelRow {
  channel: AudioChannel;
  /** Visible row label + accessible-name stem ("<label> volume"). */
  label: string;
  /** Icon shown when the channel is audible. */
  icon: string;
  /** A short, human hint of what the channel covers (kept calm, never jargon). */
  hint: string;
}

const ROWS: readonly ChannelRow[] = [
  { channel: 'sfx', label: 'Sound effects', icon: '🎵', hint: 'Reactions, doorbell & blips' },
  { channel: 'voice', label: 'Voices nearby', icon: '🎙️', hint: 'Ducks talking around you' },
  { channel: 'radio', label: 'Media', icon: '🍿', hint: 'The room’s screen — video & screen shares' },
];

/** One channel's mute toggle + volume slider — mirrors the radio mini-player control, dual-coded for a11y. */
function ChannelFader({
  row,
  prefs,
  onVolume,
  onToggleMuted,
}: {
  row: ChannelRow;
  prefs: ChannelPrefs;
  onVolume: (channel: AudioChannel, volume: number) => void;
  onToggleMuted: (channel: AudioChannel) => void;
}): JSX.Element {
  const silenced = prefs.muted || prefs.volume <= 0;
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => onToggleMuted(row.channel)}
        aria-pressed={prefs.muted}
        aria-label={prefs.muted ? `Unmute ${row.label.toLowerCase()}` : `Mute ${row.label.toLowerCase()}`}
        title={
          prefs.muted
            ? `Unmute ${row.label.toLowerCase()} (just for you)`
            : `Mute ${row.label.toLowerCase()} (just for you)`
        }
        className="rounded-lg px-1.5 py-1 text-base transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
      >
        <span aria-hidden="true">{silenced ? '🔇' : row.icon}</span>
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-xs font-medium text-white/80">{row.label}</span>
          {/* Words-not-colour readout of the level (and an explicit "Muted"), so the state never rests on
              the slider position alone. aria-hidden — the slider's own value is the SR source of truth. */}
          <span aria-hidden="true" className="shrink-0 text-[10px] tabular-nums text-white/40">
            {prefs.muted ? 'Muted' : `${prefs.volume}%`}
          </span>
        </div>
        <input
          type="range"
          min={0}
          max={100}
          value={prefs.muted ? 0 : prefs.volume}
          aria-label={`${row.label} volume`}
          onChange={(e) => onVolume(row.channel, Number(e.target.value))}
          className="h-1.5 w-full cursor-pointer accent-amber-400"
        />
      </div>
    </div>
  );
}

/**
 * The "Audio" cluster for the Me menu: three independent faders (effects / voices / radio). Each is a
 * personal, per-device comfort control — never shared, never broadcast. Reads + writes the unified audio
 * mix store directly, so the radio fader here and the radio mini-player stay perfectly in sync.
 */
export function AudioSettingsPanel(): JSX.Element {
  const sfx = useAudioSettingsStore((s) => s.sfx);
  const voice = useAudioSettingsStore((s) => s.voice);
  const radio = useAudioSettingsStore((s) => s.radio);
  const setVolume = useAudioSettingsStore((s) => s.setVolume);
  const toggleMuted = useAudioSettingsStore((s) => s.toggleMuted);
  const prefsByChannel: Record<AudioChannel, ChannelPrefs> = { sfx, voice, radio };

  return (
    <div className="flex flex-col gap-2.5" role="group" aria-label="Audio volume mix">
      {ROWS.map((row) => (
        <ChannelFader
          key={row.channel}
          row={row}
          prefs={prefsByChannel[row.channel]}
          onVolume={setVolume}
          onToggleMuted={toggleMuted}
        />
      ))}
      <p className="px-1 text-[10px] leading-snug text-white/40">
        Personal & per-device — only you hear your mix.
      </p>
    </div>
  );
}
